import { ActionIcon, Grid } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import ReactPlayer from 'react-player/lazy';
import { Show } from '../../../../../../../react-commons/components/control-flow/Show';
import type { FormNames } from '../../../../../../../react-commons/utils/form/getFormNames';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    VideoAttachmentContentPropertyValidation,
    type VideoAttachmentContentResource,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentContentResource';
import {
    VideoAttachmentPropertyValidation,
    type VideoAttachmentResource,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentResource';
import { PropertyInputs } from '../../../../reusable-components/base/property-bases/PropertyInputs';
import type { PropertyValidationInformation } from '../../../../util/PropertyValidationInformation';
import { showNotification } from '../../../../util/showNotification';

export const VideoAttachmentContent = ({
    id,
    form,
    formNames,
    contentForm,
    contentFormNames,
    readOnly,
    displayedInOverview,
}: {
    id?: string;
    form: UseFormReturn<VideoAttachmentResource>;
    formNames: FormNames<VideoAttachmentResource>;
    contentForm: UseFormReturn<VideoAttachmentContentResource>;
    contentFormNames: FormNames<VideoAttachmentContentResource>;
    readOnly: boolean;
    displayedInOverview?: boolean;
}) => {
    const getPropertyValidationInformation = (
        propertyIdentifier: VideoAttachmentPropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: id,
        resourceType: ResourceType.VIDEO_ATTACHMENT,
        propertyIdentifier,
    });

    const getContentPropertyValidationInformation = (
        propertyIdentifier: VideoAttachmentContentPropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: undefined,
        resourceType: ResourceType.VIDEO_ATTACHMENT_CONTENT,
        propertyIdentifier,
    });

    const [fileContentWatcher, fileNameWatcher] = useWatch({
        control: contentForm.control,
        name: [contentFormNames.fileContent, contentFormNames.fileName],
    });

    function _arrayBufferToBase64(buffer: ArrayBuffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    const getExtension = () => {
        const split = fileNameWatcher.split('.');
        const splitLength = split.length;
        return split[splitLength - 1];
    };

    return (
        <Grid>
            <Show when={!displayedInOverview}>
                <Grid.Col span={12}>
                    <PropertyInputs.Name
                        name={formNames.name}
                        control={form.control}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(
                            VideoAttachmentPropertyValidation.NAME
                        )}
                    />
                </Grid.Col>
            </Show>
            <Grid.Col span={12}>
                <PropertyInputs.MultilineString
                    name={formNames.description}
                    control={form.control}
                    nullable={false}
                    readOnly={readOnly}
                    propertyValidationInformation={getPropertyValidationInformation(
                        VideoAttachmentPropertyValidation.DESCRIPTION
                    )}
                />
            </Grid.Col>
            <Grid.Col span={11}>
                <PropertyInputs.String
                    name={contentFormNames.fileName}
                    control={contentForm.control}
                    nullable={false}
                    readOnly={true}
                    propertyValidationInformation={getContentPropertyValidationInformation(
                        VideoAttachmentContentPropertyValidation.FILE_NAME
                    )}
                />
            </Grid.Col>
            <Grid.Col span={1}>
                <ActionIcon
                    component="label"
                    variant={'transparent'}
                    size={'small'}
                    disabled={readOnly}
                    color={'var(--mantine-color-base-button)'}
                    pt={30}
                >
                    <IconUpload />
                    <input
                        hidden
                        accept={'video/*'}
                        type={'file'}
                        onChange={(e) => {
                            const files = e.target.files;
                            const fileNames: Array<string> = [];
                            if (files && files.length > 0) {
                                Array.from(files).forEach((file) => {
                                    fileNames.push(file.name);
                                });
                                if ((files.item(0)?.size ?? 0) > 7 * 1024 * 1024) {
                                    showNotification({ title: 'Video File size is too large!', severity: 'error' });
                                } else {
                                    void files
                                        .item(0)
                                        ?.arrayBuffer()
                                        .then((buffer) => {
                                            contentForm.setValue(
                                                contentFormNames.fileContent,
                                                _arrayBufferToBase64(buffer),
                                                { shouldDirty: true }
                                            );
                                            contentForm.setValue(contentFormNames.fileName, fileNames[0], {
                                                shouldDirty: true,
                                            });
                                        });
                                }
                            }
                        }}
                    />
                </ActionIcon>
            </Grid.Col>
            <Show when={fileNameWatcher && fileContentWatcher}>
                <Grid.Col span={12}>
                    <ReactPlayer
                        width={'100%'}
                        url={`data:video/${getExtension()};base64,${fileContentWatcher}`}
                        controls
                    />
                </Grid.Col>
            </Show>
        </Grid>
    );
};
